/**
 * Campaign-scoped persisted notes and session-summary history for the SessionView.
 *
 * WHO: The authenticated participant reading a campaign may browse its authorized history; any
 *       verified current participant may save a note while the current session is active/paused.
 * WHAT: Separates explicit saved notes from realtime chat; server responses alone acknowledge saves.
 * WHERE: Reads/writes through CampaignHistoryService and merges only the accepted CampaignRealtime
 *        snapshot history. Backend membership checks remain authoritative for every request.
 * WHEN: Fetches are abortable and scoped by campaign/session/viewer; old-page and snapshot updates
 *        merge by entry_id so reconnect snapshots cannot erase already-loaded older pages. A failed
 *        save retains exact text and UUID so retry is idempotent; editing text starts a new request.
 * WHY: PRD004.8 requires durable, honest saved history while ordinary chat remains a separate
 *      deferred surface. Ended or unconfirmed sessions cannot accept notes in this client.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Heading, HStack, Spinner, Text, Textarea, VStack } from '@chakra-ui/react';
import { ApiError } from '../../services/api/ApiError';
import { campaignHistoryService, type CampaignHistoryEntry } from '../../services/api/CampaignHistoryService';
import { theme as mythboundTheme } from '../../config/theme';

interface CampaignHistoryProps {
  campaignId: string | null;
  sessionId: string | null;
  viewerId: string | null;
  sessionStatus: 'scheduled' | 'active' | 'paused' | 'ended' | null;
  snapshotEntries: CampaignHistoryEntry[];
  onRefreshSnapshot?: () => void;
  isOpen: boolean;
}

const EMPTY_ENTRIES: CampaignHistoryEntry[] = [];
const MAX_NOTE_LENGTH = 4000;

function mergeEntries(...groups: CampaignHistoryEntry[][]): CampaignHistoryEntry[] {
  const entries = new Map<string, CampaignHistoryEntry>();
  for (const group of groups) for (const entry of group) entries.set(entry.entry_id, entry);
  const sorted = Array.from(entries.values()).sort((left, right) => {
    const byDate = Date.parse(left.created_at) - Date.parse(right.created_at);
    return byDate || left.entry_id.localeCompare(right.entry_id);
  });
  return sorted;
}

function makeEntryId(): string {
  if (!globalThis.crypto?.getRandomValues) throw new Error('Secure note IDs are unavailable in this browser.');
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const CampaignHistory: React.FC<CampaignHistoryProps> = ({
  campaignId, sessionId, viewerId, sessionStatus, snapshotEntries = EMPTY_ENTRIES,
  onRefreshSnapshot, isOpen,
}) => {
  const scopeKey = `${campaignId ?? ''}\u0000${sessionId ?? ''}\u0000${viewerId ?? ''}`;
  const scopeRef = useRef(scopeKey);
  const previousScope = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const generation = useRef(0);
  const mainController = useRef<AbortController | null>(null);
  const olderController = useRef<AbortController | null>(null);
  const saveController = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const pendingSave = useRef<{ scopeKey: string; entryId: string; text: string } | null>(null);
  const [entriesState, setEntriesState] = useState<{ scopeKey: string; entries: CampaignHistoryEntry[] }>({ scopeKey: '', entries: [] });
  const [cursorState, setCursorState] = useState<{ scopeKey: string; cursor: string | null }>({ scopeKey: '', cursor: null });
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [draftState, setDraftState] = useState<{ scopeKey: string; text: string }>({ scopeKey: '', text: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      saveController.current?.abort();
      saveController.current = null;
    };
  }, []);

  const entries = entriesState.scopeKey === scopeKey ? entriesState.entries : EMPTY_ENTRIES;
  const nextBeforeSessionId = cursorState.scopeKey === scopeKey ? cursorState.cursor : null;
  // Hide a prior session/account draft during render, before the scope-change effect can clear it.
  const draft = draftState.scopeKey === scopeKey ? draftState.text : '';
  const canSave = Boolean(campaignId && sessionId && viewerId && (sessionStatus === 'active' || sessionStatus === 'paused'));
  const trimmedDraft = draft.trim();
  const noteLength = Array.from(trimmedDraft).length;
  const noteValid = noteLength >= 1 && noteLength <= MAX_NOTE_LENGTH;

  useEffect(() => {
    if (previousScope.current === scopeKey) return;
    previousScope.current = scopeKey;
    // A campaign, session, or verified-account change drops both private draft text and its
    // retry UUID; those values must never follow a user into another resource scope.
    pendingSave.current = null;
    setDraftState({ scopeKey, text: '' });
    setSaveError(null);
    setSaving(false);
    // Scope cleanup aborts older-page work; reset its view-owned spinner because the fenced finally block cannot clear it.
    setLoadingOlder(false);
    setCursorState({ scopeKey, cursor: null });
  }, [scopeKey]);

  // Realtime snapshots contain only the latest curated slice. Merge by stable ID to add new
  // arrivals while retaining every older page this view already fetched.
  useEffect(() => {
    setEntriesState((current) => {
      const base = current.scopeKey === scopeKey ? current.entries : [];
      const merged = mergeEntries(base, snapshotEntries);
      if (current.scopeKey === scopeKey && merged.length === current.entries.length && merged.every((entry, index) => entry === current.entries[index])) return current;
      return { scopeKey, entries: merged };
    });
  }, [scopeKey, snapshotEntries]);

  // Fetch the initial HTTP page only while the history surface is open. Scope changes
  // immediately hide old data during render, then abort requests and clear unsaved drafts.
  useEffect(() => {
    if (!isOpen || !campaignId || !viewerId) return undefined;
    const requestGeneration = ++generation.current;
    const controller = new AbortController();
    mainController.current = controller;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setSaving(false);
    // Scope cleanup aborts older-page work; reset its view-owned spinner because the fenced finally block cannot clear it.
    setLoadingOlder(false);
    setCursorState({ scopeKey, cursor: null });

    void campaignHistoryService.getCampaignHistory(campaignId, null, { signal: controller.signal }).then((page) => {
      if (controller.signal.aborted || requestGeneration !== generation.current || scopeRef.current !== scopeKey) return;
      setEntriesState((current) => ({
        scopeKey,
        entries: mergeEntries(current.scopeKey === scopeKey ? current.entries : [], page.entries),
      }));
      setCursorState({ scopeKey, cursor: page.next_before_session_id });
    }).catch((error: unknown) => {
      if (controller.signal.aborted || requestGeneration !== generation.current || scopeRef.current !== scopeKey) return;
      setLoadError(error instanceof Error ? error.message : 'Could not load saved history.');
    }).finally(() => {
      if (requestGeneration === generation.current && scopeRef.current === scopeKey) setLoading(false);
    });

    return () => {
      controller.abort();
      if (mainController.current === controller) mainController.current = null;
      olderController.current?.abort();
      olderController.current = null;
      saveController.current?.abort();
      saveController.current = null;
      generation.current += 1;
    };
  }, [isOpen, campaignId, viewerId, scopeKey, retry]);

  const loadOlder = useCallback(async () => {
    if (!campaignId || !viewerId || !nextBeforeSessionId || loadingOlder) return;
    const requestGeneration = generation.current;
    const controller = new AbortController();
    olderController.current = controller;
    setLoadingOlder(true);
    setLoadError(null);
    try {
      const page = await campaignHistoryService.getCampaignHistory(campaignId, nextBeforeSessionId, { signal: controller.signal });
      if (controller.signal.aborted || requestGeneration !== generation.current || scopeRef.current !== scopeKey) return;
      setEntriesState((current) => ({
        scopeKey,
        entries: mergeEntries(current.scopeKey === scopeKey ? current.entries : [], page.entries),
      }));
      setCursorState({ scopeKey, cursor: page.next_before_session_id });
    } catch (error) {
      if (!controller.signal.aborted && requestGeneration === generation.current && scopeRef.current === scopeKey) {
        setLoadError(error instanceof Error ? error.message : 'Could not load older history.');
      }
    } finally {
      if (olderController.current === controller) olderController.current = null;
      if (requestGeneration === generation.current && scopeRef.current === scopeKey) setLoadingOlder(false);
    }
  }, [campaignId, viewerId, nextBeforeSessionId, loadingOlder, scopeKey]);

  const handleDraftChange = (value: string) => {
    if (pendingSave.current && (pendingSave.current.scopeKey !== scopeKey || value.trim() !== pendingSave.current.text)) pendingSave.current = null;
    setDraftState({ scopeKey, text: value });
    setSaveError(null);
  };

  const saveNote = async () => {
    if (!canSave || !campaignId || !sessionId || !viewerId || !noteValid || saving) return;
    let request = pendingSave.current;
    if (!request || request.scopeKey !== scopeKey || request.text !== trimmedDraft) {
      try {
        request = { scopeKey, entryId: makeEntryId(), text: trimmedDraft };
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : 'Could not create a safe retry ID.');
        return;
      }
      pendingSave.current = request;
    }
    const requestScope = scopeKey;
    const controller = new AbortController();
    saveController.current = controller;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await campaignHistoryService.saveSessionNote(sessionId, request.entryId, request.text, { signal: controller.signal });
      if (!mounted.current || controller.signal.aborted || scopeRef.current !== requestScope || saved.session_id !== sessionId) return;
      // Only a validated server response becomes visible as saved. The retained request ID
      // makes a retry after a lost response safe and backend-idempotent.
      setEntriesState((current) => ({
        scopeKey: requestScope,
        entries: mergeEntries(current.scopeKey === requestScope ? current.entries : [], [saved]),
      }));
      setDraftState({ scopeKey, text: '' });
      pendingSave.current = null;
      onRefreshSnapshot?.();
    } catch (error) {
      if (!mounted.current || controller.signal.aborted || scopeRef.current !== requestScope) return;
      const conflict = error instanceof ApiError && error.status === 409;
      setSaveError(conflict
        ? 'The session changed or ended before the note was saved. Refresh the session state and try again if notes are still available.'
        : error instanceof Error ? error.message : 'Could not save this note. Your text is kept so you can retry.');
    } finally {
      if (saveController.current === controller) saveController.current = null;
      if (mounted.current && !controller.signal.aborted && scopeRef.current === requestScope) setSaving(false);
    }
  };

  if (!isOpen) return null;
  const noScope = !campaignId || !sessionId || !viewerId;
  return (
    <Box role="region" aria-label="Saved notes and campaign history" mt={6} p={5} bg="#17171d" borderWidth="1px" borderColor={mythboundTheme.colors.accent.brown} borderRadius="md">
      <Heading size="md" color={mythboundTheme.colors.primary.gold} mb={4}>Saved Notes &amp; Campaign History</Heading>
      {noScope ? <Text role="status" color={mythboundTheme.colors.accent.silver}>Confirming the current campaign, session, and signed-in account before loading saved history.</Text> : (
        <>
          {loading && entries.length === 0 && <HStack role="status"><Spinner size="sm" /><Text color={mythboundTheme.colors.accent.silver}>Loading saved history…</Text></HStack>}
          {loadError && (
            <Alert status="error" mb={3}>
              <Text flex="1">{loadError}</Text>
              <Button size="sm" onClick={() => setRetry((value) => value + 1)}>Retry</Button>
            </Alert>
          )}
          {entries.length === 0 && !loading && !loadError && <Text color={mythboundTheme.colors.accent.silver}>No saved notes or session summaries yet.</Text>}
          <VStack align="stretch" spacing={3} mb={4}>
            {entries.map((entry) => (
              <Box key={entry.entry_id} p={3} bg="rgba(245,245,247,0.04)" borderRadius="md" borderWidth="1px" borderColor="rgba(197,197,199,0.16)">
                <HStack justify="space-between" align="start" mb={1}>
                  <Text color={mythboundTheme.colors.primary.main} fontWeight="bold">{entry.type === 'session_note' ? 'Saved note' : 'Session summary'} · {entry.author}</Text>
                  <Text color={mythboundTheme.colors.accent.silver} fontSize="xs">{new Date(entry.created_at).toLocaleString()}</Text>
                </HStack>
                <Text whiteSpace="pre-wrap" color={mythboundTheme.colors.secondary.white}>{entry.text}</Text>
              </Box>
            ))}
          </VStack>
          {nextBeforeSessionId && (
            <Button size="sm" variant="outline" onClick={() => void loadOlder()} isLoading={loadingOlder} loadingText="Loading…" mb={4}>
              Load older history
            </Button>
          )}
          {canSave ? (
            <FormControl>
              <FormLabel color={mythboundTheme.colors.secondary.white}>Add a saved note to this session</FormLabel>
              <Textarea value={draft} onChange={(event) => handleDraftChange(event.target.value)} disabled={saving} rows={4} placeholder="Write a note to keep with this campaign’s session history…" aria-describedby="campaign-history-note-status" />
              <HStack justify="space-between" mt={2}>
                <Text id="campaign-history-note-status" color={noteLength > MAX_NOTE_LENGTH ? 'red.300' : mythboundTheme.colors.accent.silver} fontSize="sm">{noteLength}/{MAX_NOTE_LENGTH} characters</Text>
                <Button onClick={() => void saveNote()} isLoading={saving} loadingText="Saving…" isDisabled={!noteValid || saving}>Save note</Button>
              </HStack>
              {saveError && <Text role="alert" color="red.300" mt={2}>{saveError} The text is still here; retry keeps the same request ID unless you edit it.</Text>}
            </FormControl>
          ) : (
            <Text color={mythboundTheme.colors.accent.silver} role="status">
              {sessionStatus === 'ended' ? 'This session has ended; saved history remains available, but new notes are closed.' : sessionStatus === 'scheduled' ? 'Notes open when the session is active or paused.' : 'Notes are disabled until this session’s current state is confirmed.'}
            </Text>
          )}
        </>
      )}
    </Box>
  );
};

export default CampaignHistory;
